import { useState, type FormEvent } from "react";
import { useApiTokens, useCreateApiToken, useDeleteApiToken } from "../hooks/useApi";
import { formatDateTime } from "../lib/units";

export function ApiTokensPanel() {
  const { data: tokens, isLoading } = useApiTokens();
  const create = useCreateApiToken();
  const del = useDeleteApiToken();
  const [name, setName] = useState("");

  function handleCreate(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    create.mutate(trimmed, { onSuccess: () => setName("") });
  }

  async function handleDelete(id: string, tokenName: string) {
    if (!confirm(`Revoke "${tokenName}"? Anything using it will stop working immediately.`)) return;
    await del.mutateAsync(id);
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-night-800 dark:bg-night-900">
      <h2 className="mb-1 font-medium">API tokens</h2>
      <p className="mb-4 text-sm text-slate-500 dark:text-night-400">
        For letting another app upload rides directly, without signing in through Authentik. Send it as{" "}
        <code className="rounded bg-slate-100 px-1 py-0.5 text-xs dark:bg-night-800">Authorization: Bearer &lt;token&gt;</code>.
        Full API reference: <a href="/api/docs" className="text-brand-600 hover:underline dark:text-brand-400">/api/docs</a> (
        <a href="/api/openapi.json" className="text-brand-600 hover:underline dark:text-brand-400">openapi.json</a>).
      </p>

      {create.data && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950">
          <p className="mb-2 font-medium text-amber-800 dark:text-amber-300">
            Copy this token now — you won't be able to see it again.
          </p>
          <code className="block overflow-x-auto whitespace-nowrap rounded bg-white px-2 py-1.5 text-xs dark:bg-night-900">
            {create.data.token}
          </code>
        </div>
      )}

      <form onSubmit={handleCreate} className="mb-4 flex gap-2">
        <input
          type="text"
          placeholder="Token name, e.g. Recording app"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-night-700 dark:bg-night-900"
        />
        <button
          type="submit"
          disabled={create.isPending || !name.trim()}
          className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-700 disabled:opacity-50"
        >
          Create
        </button>
      </form>

      {isLoading && <p className="text-sm text-slate-500 dark:text-night-400">Loading…</p>}
      {tokens && tokens.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-night-400">No API tokens yet.</p>
      )}
      {tokens && tokens.length > 0 && (
        <ul className="divide-y divide-slate-100 dark:divide-night-800">
          {tokens.map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{t.name}</div>
                <div className="text-xs text-slate-500 dark:text-night-400">
                  ····{t.lastFour} · created {formatDateTime(t.createdAt)} ·{" "}
                  {t.lastUsedAt ? `last used ${formatDateTime(t.lastUsedAt)}` : "never used"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void handleDelete(t.id, t.name)}
                className="shrink-0 rounded-lg px-2 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
